import { useEffect, useId, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  getDailySummarySchedule, getPermittedNumbers, updateDailySummarySchedule,
  sendDailySummaryWhatsAppNow, startPermittedNumberVerification,
  updatePermittedNumber, deletePermittedNumber,
  type DailySummarySchedule, type PermittedNumber,
} from '../api/client';
import { useAuth } from '../context/AuthContext';

export function useDailySummarySettings(providedNumbers?: PermittedNumber[], recipientsBusy = false) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [schedule, setSchedule] = useState<DailySummarySchedule | null>(null);
  const [loadedNumbers, setLoadedNumbers] = useState<PermittedNumber[]>([]);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [revision, setRevision] = useState(0);
  const externallyManaged = providedNumbers !== undefined;
  const numbers = providedNumbers ?? loadedNumbers;
  const busy = loading || saving || recipientsBusy;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setMessage('');
    setSchedule(null);
    setLoadedNumbers([]);
    setPhone('');
    Promise.all([
      getDailySummarySchedule(),
      externallyManaged ? Promise.resolve(null) : getPermittedNumbers(),
    ]).then(([configuration, recipients]) => {
      if (!active) return;
      setSchedule(configuration.data);
      if (recipients) setLoadedNumbers(recipients.data.numbers);
    }).catch(() => {
      if (active) setError(t('reports.whatsappSummary.loadError'));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [externallyManaged, revision, t, user?.tenant_id]);

  async function run(action: () => Promise<void>, successKey?: string) {
    if (busy) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await action();
      if (successKey) setMessage(t(successKey));
    } catch (failure) {
      const detail = axios.isAxiosError<{ detail?: unknown }>(failure)
        ? failure.response?.data?.detail : undefined;
      setError(typeof detail === 'string' ? detail : t('reports.whatsappSummary.actionError'));
    } finally {
      setSaving(false);
    }
  }

  return {
    schedule, setSchedule, numbers, phone, setPhone, loading, busy, error, message,
    reload: () => setRevision(value => value + 1),
    save: () => run(async () => {
      if (!schedule) throw new Error('Daily summary configuration is not loaded');
      const response = await updateDailySummarySchedule(schedule);
      setSchedule(response.data);
    }, 'settings.permittedNumbers.scheduleSaved'),
    send: () => run(async () => {
      await sendDailySummaryWhatsAppNow();
    }, 'settings.permittedNumbers.testSent'),
    verify: () => run(async () => {
      const response = await startPermittedNumberVerification({ phone_number: phone.trim() });
      setLoadedNumbers(response.data.numbers);
      setPhone('');
    }, 'settings.permittedNumbers.verificationSent'),
    toggle: (number: PermittedNumber, enabled: boolean) => run(async () => {
      const response = await updatePermittedNumber(number.phone_number, { daily_summary: enabled });
      setLoadedNumbers(response.data.numbers);
    }),
    remove: (number: PermittedNumber) => run(async () => {
      const response = await deletePermittedNumber(number.phone_number);
      setLoadedNumbers(response.data.numbers);
    }, 'settings.permittedNumbers.removeSuccess'),
  };
}

type Controller = ReturnType<typeof useDailySummarySettings>;

export function DailySummarySettingsForm({
  controller, showRecipients = true,
}: { controller: Controller; showRecipients?: boolean }) {
  const { t } = useTranslation();
  const id = useId();
  const { schedule, busy, numbers } = controller;
  const enabled = numbers.some(number => number.status === 'verified' && number.daily_summary);
  const fields = ['send_hour', 'send_minute', 'timezone'] as const;
  const labels = ['scheduleHour', 'scheduleMinute', 'scheduleTimezone'];
  const zones = ['Asia/Jerusalem', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'UTC'];

  return <div className="space-y-4 rounded-md border border-gray-100 bg-gray-50 p-4">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold">{t('settings.permittedNumbers.scheduleTitle')}</h3>
        <p className="text-xs text-gray-500">{t('settings.permittedNumbers.scheduleDescription')}</p>
      </div>
      <button type="button" onClick={controller.send} disabled={busy || !schedule || !enabled}
        title={enabled ? t('settings.permittedNumbers.sendNowTooltip') : t('reports.whatsappSummary.noEnabledRecipientsHint')}
        className="rounded-md border bg-white px-3 py-2 text-sm disabled:opacity-50">
        {t('settings.permittedNumbers.sendNow')}
      </button>
    </div>
    {controller.loading && <p role="status">{t('reports.whatsappSummary.loading')}</p>}
    {controller.error && <p role="alert" className="text-sm text-red-700">{controller.error}</p>}
    {controller.message && <p role="status" className="text-sm text-green-700">{controller.message}</p>}
    {schedule && <>
      <div className="grid gap-3 sm:grid-cols-[100px_100px_1fr]">
        {fields.map((field, index) => {
          const options = field === 'timezone'
            ? Array.from(new Set([schedule.timezone, ...zones]))
            : Array.from({ length: field === 'send_hour' ? 24 : 60 }, (_, value) => value);
          return <div key={field}>
            <label htmlFor={`${id}-${field}`} className="mb-1 block text-xs font-medium">{t(`settings.permittedNumbers.${labels[index]}`)}</label>
            <select id={`${id}-${field}`} value={schedule[field]} disabled={busy}
              onChange={event => controller.setSchedule({ ...schedule, [field]: field === 'timezone' ? event.target.value : Number(event.target.value) })}
              className="w-full rounded-md border bg-white px-2 py-2 text-sm disabled:opacity-50">
              {options.map(option => <option key={option} value={option}>{typeof option === 'number' ? String(option).padStart(2, '0') : option}</option>)}
            </select>
          </div>;
        })}
      </div>
      <fieldset disabled={busy}>
        <legend className="mb-2 text-xs font-medium">{t('settings.permittedNumbers.scheduleDays')}</legend>
        <div className="flex flex-wrap gap-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
            const selected = schedule.send_days.includes(index);
            return <button key={day} type="button" aria-pressed={selected}
              onClick={() => controller.setSchedule({ ...schedule, send_days: selected
                ? schedule.send_days.filter(value => value !== index) : [...schedule.send_days, index].sort() })}
              className={`rounded-md border px-3 py-2 text-xs ${selected ? 'border-primary-600 bg-primary-600 text-white' : 'bg-white'}`}>
              {t(`settings.permittedNumbers.day${day}`)}
            </button>;
          })}
        </div>
      </fieldset>
      <button type="button" onClick={controller.save} disabled={busy || schedule.send_days.length === 0}
        className="rounded-md bg-primary-600 px-4 py-2 text-sm text-white disabled:opacity-50">
        {t('settings.permittedNumbers.scheduleSave')}
      </button>
    </>}
    {showRecipients && <section className="space-y-3" aria-label={t('settings.permittedNumbers.title')}>
      <p className="text-sm text-gray-600">{t('settings.permittedNumbers.description')}</p>
      <label htmlFor={`${id}-phone`} className="block text-sm font-medium">{t('settings.permittedNumbers.phoneNumber')}</label>
      <div className="flex gap-2">
        <input id={`${id}-phone`} type="tel" value={controller.phone} onChange={event => controller.setPhone(event.target.value)}
          placeholder="+972501234567" disabled={busy || !schedule} className="min-w-0 flex-1 rounded-md border px-3 py-2" />
        <button type="button" onClick={controller.verify} disabled={busy || !schedule || !controller.phone.trim()}
          className="rounded-md border bg-white px-3 py-2 text-sm disabled:opacity-50">{t('settings.permittedNumbers.add')}</button>
      </div>
      {!controller.loading && !controller.error && numbers.length === 0 && <p className="text-sm">{t('settings.permittedNumbers.empty')}</p>}
      {numbers.map(number => <div key={number.phone_number} className="flex flex-wrap items-center gap-3 rounded-md border bg-white p-3">
        <span dir="ltr">{number.phone_number}</span>
        <span className="text-xs">{t(`settings.permittedNumbers.${number.status === 'verified' ? 'verified' : 'pending'}`)}</span>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={number.daily_summary} disabled={busy || number.status !== 'verified'}
            onChange={event => void controller.toggle(number, event.target.checked)} />
          {t('settings.permittedNumbers.dailySummaryToggle')}
        </label>
        <button type="button" onClick={() => void controller.remove(number)} disabled={busy}
          className="rounded-md border border-red-200 px-2 py-1 text-sm text-red-700 disabled:opacity-50">{t('common.delete')}</button>
      </div>)}
    </section>}
    <button type="button" onClick={controller.reload} disabled={busy} className="text-sm text-primary-700 underline disabled:opacity-50">
      {t('reports.whatsappSummary.reload')}
    </button>
  </div>;
}

export default function DailySummarySettings() {
  const controller = useDailySummarySettings();
  return <DailySummarySettingsForm controller={controller} />;
}
